import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { Trans, useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartNetwork, faChess, faGlobe, faPlus, faRotate, faShieldCheck, faShieldExclamation, faTrash, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { Modal, Button, Form, Input, InputNumber, Tabs, Empty, Card, Flex, Checkbox, Select, Tooltip } from 'antd';
import { PREVIEW_AI_QUERY } from 'graph/documents';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP);

const formLayout = {
	labelCol: { span: 24 },
	wrapperCol: {
		span: 24
	}
};

import "./ProjectModal.sass";
import FormWeight from 'components/controls/FormWeight';
import { AttachmentsSection } from 'components/AttachmentsSection';
import { ProjectShareTab } from 'components/Modal/ProjectShareTab';
import { useQuery, useSubscription } from '@apollo/client';
import { LIST_PROJECT_ATTACHMENTS, SUBSCRIBE_TO_ATTACHMENT_STATUS } from 'graph/documents';
import { AI_GENERATION_ENABLED } from 'config/featureFlags';

export type AiHorizonUnit = 'd' | 'w' | 'm' | 'y';

export interface AiScenarioInput {
	id?: string;
	label: string;
	content: string;
	horizonValue: number;
	horizonUnit: AiHorizonUnit;
}

interface ProjectModalValues {
	name: string;
	reference: string;
	description: string;
	descriptionLong: string;
	tags: any[];
	riskCategories: any[];
	likelihoods: string;
	impacts: string;
	type: string | null;
	aiGenerate?: boolean;
	aiContext?: string;
	aiScenarios?: AiScenarioInput[];
}

const HORIZON_UNIT_OPTIONS = (t: any) => [
	{ value: 'd', label: t('models.project.fields.ai.horizon_unit.d', { defaultValue: 'jours' }) },
	{ value: 'w', label: t('models.project.fields.ai.horizon_unit.w', { defaultValue: 'semaines' }) },
	{ value: 'm', label: t('models.project.fields.ai.horizon_unit.m', { defaultValue: 'mois' }) },
	{ value: 'y', label: t('models.project.fields.ai.horizon_unit.y', { defaultValue: 'années' }) }
];

const defaultAiScenarios = (t: any): AiScenarioInput[] => [
	{
		label: t('models.project.fields.ai.scenarios.most_likely_label', { defaultValue: 'Le plus probable' }),
		content: t('models.project.fields.ai.scenarios.most_likely_default', {
			defaultValue: "Trajectoire la plus probable si les tendances actuelles se poursuivent : ni catastrophe, ni euphorie — la projection médiane des risques et des opportunités déjà identifiés."
		}),
		horizonValue: 6,
		horizonUnit: 'm'
	}
];
interface ProjectModalProps {
	id?: string;
	name?: string;
	values?: ProjectModalValues;
	onSave: (values: any) => Promise<string | void>;
	onChange?: (id: string, values: any) => void;
	onHide: () => void;
	authorizedTypes: {
		pro: boolean;
		stakeholders: boolean;
		risks: boolean;
	};
}

const defaultLikelihoods = (t: any) => {
	return [
		{ id: '1', name: t('models.risk.likelihoods.1') },
		{ id: '2', name: t('models.risk.likelihoods.2') },
		{ id: '3', name: t('models.risk.likelihoods.3') },
		{ id: '4', name: t('models.risk.likelihoods.4') },
		{ id: '5', name: t('models.risk.likelihoods.5') }
	]
}
const defaultImpacts = (t: any) => {
	return [
		{ id: '1', name: t('models.risk.impacts.1') },
		{ id: '2', name: t('models.risk.impacts.2') },
		{ id: '3', name: t('models.risk.impacts.3') },
		{ id: '4', name: t('models.risk.impacts.4') },
		{ id: '5', name: t('models.risk.impacts.5') }
	]
}
const defaultRiskCategories = (t: any) => {
	return [
		{ id: '1', name: t('models.risk_category.default_values.name.1'), weight: 3 },
		{ id: '2', name: t('models.risk_category.default_values.name.2'), weight: 2 },
		{ id: '3', name: t('models.risk_category.default_values.name.3'), weight: 1 }
	]
}
// Catégories d'indicateurs par défaut : les 4 perspectives du Balanced
// Scorecard (Finance, Clients, Processus internes, Apprentissage & croissance).
const defaultIndicatorCategories = (t: any) => {
	return [
		{ id: '1', name: t('models.indicator_category.default_values.name.1'), weight: 1 },
		{ id: '2', name: t('models.indicator_category.default_values.name.2'), weight: 1 },
		{ id: '3', name: t('models.indicator_category.default_values.name.3'), weight: 1 },
		{ id: '4', name: t('models.indicator_category.default_values.name.4'), weight: 1 }
	]
}

// Lazy uuid v4 — only generated once per modal opening, when the user
// actually starts dropping a file in creation mode.
const newUuid = (): string => {
	if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
		return (crypto as any).randomUUID();
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

export const ProjectModal = (props: ProjectModalProps) => {
	const [form] = Form.useForm();
	const { t, i18n } = useTranslation();
	const apollo = useApolloClient();
	const [type, setType] = React.useState<any | null>(props.values?.type);

	// In creation mode, attachments are staged under this session id. They
	// get tagged temp=true on S3 (1-day expiry) until the project is created
	// and the parent page (Projects) calls claimAttachments to bind them
	// and remove the expiry tag — done before kicking off any AI flow so
	// the analyze lambda doesn't race the claim.
	const [tempSessionId] = React.useState<string>(() => (props.id ? '' : newUuid()));

	// Watch attachment scan status: block the form submit while any file is
	// still being uploaded or scanned. Avoids the user kicking off AI
	// generation with a file that hasn't been antivirus-cleared yet (the
	// analyze lambda would silently skip non-ready files).
	const attachmentsScope = props.id || tempSessionId
		? { projectId: props.id, tempSessionId: !props.id ? tempSessionId : undefined }
		: null;
	const { data: attachmentsStatusData, refetch: refetchAttachmentsStatus } = useQuery<{ listProjectAttachments: any[] }>(
		LIST_PROJECT_ATTACHMENTS,
		{
			variables: attachmentsScope || {},
			skip: !attachmentsScope,
			fetchPolicy: 'cache-and-network'
		}
	);
	useSubscription(SUBSCRIBE_TO_ATTACHMENT_STATUS, {
		variables: attachmentsScope || {},
		skip: !attachmentsScope,
		onData: () => { refetchAttachmentsStatus(); }
	});
	const attachmentsInflight = (attachmentsStatusData?.listProjectAttachments || []).some(
		(a: any) => a.status === 'pending' || a.status === 'scanning'
	);

	// Preview state for the neutral reformulation sent to public AI
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const previewDebounceRef = useRef<number | null>(null);
	const descLongWarningAcknowledgedRef = useRef(false);

	const aiGenerateWatch = Form.useWatch('aiGenerate', form);
	const nameWatch = Form.useWatch('name', form);
	const descriptionWatch = Form.useWatch('description', form);
	const descriptionLongWatch = Form.useWatch('descriptionLong', form);
	const aiContextWatch = Form.useWatch('aiContext', form);

	const labelWithPrivacyChip = (text: string) => (
		<span className="ai-label-with-chip">
			<span>{text}</span>
			{aiGenerateWatch && (
				<Tooltip
					color="rgba(183, 235, 143, 0.92)"
					overlayInnerStyle={{ color: '#1f4d0a' }}
					overlayClassName="ai-zone-tooltip private"
					title={t('messages.ai_project_generation.field_private_tooltip', {
						defaultValue: "Traité par une IA privée, dans un environnement cloisonné. Pas de réentraînement, aucune information transmise à l'extérieur."
					})}
				>
					<span className="ai-zone-label private xs">
						<FontAwesomeIcon icon={faShieldCheck as any} className="ai-zone-icon" />
						{t('messages.ai_project_generation.field_private_chip', { defaultValue: 'Privé & sécurisé' })}
					</span>
				</Tooltip>
			)}
		</span>
	);

	const runPreview = React.useCallback(async () => {
		const name = (form.getFieldValue('name') || '').trim();
		if (!name) {
			form.setFieldValue('aiSearchPreview', '');
			return;
		}
		setPreviewLoading(true);
		setPreviewError(null);
		try {
			const res = await apollo.query({
				query: PREVIEW_AI_QUERY,
				variables: {
					name,
					mission: form.getFieldValue('descriptionLong') || null,
					description: form.getFieldValue('description') || null,
					context: form.getFieldValue('aiContext') || null,
					language: i18n.language?.split('-')[0] || 'fr'
				},
				fetchPolicy: 'no-cache'
			});
			form.setFieldValue('aiSearchPreview', res.data?.previewAiQuery || '');
		} catch (e) {
			console.error('previewAiQuery failed', e);
			setPreviewError(e instanceof Error ? e.message : String(e));
		} finally {
			setPreviewLoading(false);
		}
	}, [apollo, form, i18n.language]);

	// Debounced auto-refresh whenever the inputs or the AI checkbox change
	useEffect(() => {
		if (!aiGenerateWatch) return;
		if (previewDebounceRef.current) window.clearTimeout(previewDebounceRef.current);
		previewDebounceRef.current = window.setTimeout(() => {
			void runPreview();
		}, 800);
		return () => {
			if (previewDebounceRef.current) window.clearTimeout(previewDebounceRef.current);
		};
	}, [aiGenerateWatch, nameWatch, descriptionWatch, descriptionLongWatch, aiContextWatch, runPreview]);

	console.log('ProjectModal', props);

	const handleFormSubmit = async () => {
		if (submitting) return;
		console.log('getFieldsValue(true)', form.getFieldsValue(true));
		console.log('getFieldsValue', form.getFieldsValue());
		setSubmitting(true);
		try {
			const values = await form.validateFields();
			console.log('validateFields', values);
			// Pass the tempSessionId through values so the caller can claim
			// attachments BEFORE kicking off any downstream work (eg. AI
			// analysis) — otherwise the analyze lambda races the claim
			// and reads zero attachments.
			if (!props.id && tempSessionId) {
				(values as any)._tempSessionId = tempSessionId;
			}
			await props.onSave(values);
			props.onHide();
		} catch (errorInfo) {
			console.log('error submitting form', errorInfo);
			setSubmitting(false);
		}
	};

	const submitDisabled = attachmentsInflight || submitting;
	const submitButton = type ? (
		<Button
			onClick={handleFormSubmit}
			type="primary"
			disabled={submitDisabled}
			loading={submitting}
			icon={aiGenerateWatch ? <FontAwesomeIcon icon={faWandMagicSparkles as any} /> : undefined}
		>
			{aiGenerateWatch
				? t('messages.ai_project_generation.launch_generation', { defaultValue: 'Lancer la génération' })
				: t('actions.save')}
		</Button>
	) : null;
	const footer = (
		<div style={{ display: 'flex' }}>
			<Button
				style={{ marginRight: 'auto' }}
				onClick={() => {
					props.onHide();
				}}
			>
				{t('actions.close')}
			</Button>
			{submitDisabled ? (
				<Tooltip title={t('messages.attachments.submit_blocked', { defaultValue: 'Attendez la fin du scan antivirus de toutes les pièces jointes avant de lancer.' })}>
					<span>{submitButton}</span>
				</Tooltip>
			) : (
				submitButton
			)}
		</div>
	);

	const likelihoods = props.values?.likelihoods && props.values?.likelihoods.length > 0 ? props.values?.likelihoods : defaultLikelihoods(t);
	const impacts = props.values?.impacts && props.values?.impacts.length > 0 ? props.values?.impacts : defaultImpacts(t);
	const riskCategories = props.values?.riskCategories && props.values?.riskCategories.length > 0 ? props.values?.riskCategories : props.id ? [] : defaultRiskCategories(t);
	// Défauts BSC seulement en création (pas props.id) — en édition on montre
	// les catégories existantes du projet.
	const indicatorCategories = props.values?.indicatorCategories && props.values?.indicatorCategories.length > 0 ? props.values?.indicatorCategories : props.id ? [] : defaultIndicatorCategories(t);

	const applyProjectTypeHover = (e: React.MouseEvent<HTMLDivElement>) => {

		gsap.to(e.currentTarget, {
			scale: 1.05,
			duration: 0.5,
			ease: "power2.inOut",
			y: -10,
		})
	}

	const removeProjectTypeHover = (e: React.MouseEvent<HTMLDivElement>) => {
		gsap.to(e.currentTarget, {
			scale: 1,
			duration: 0.5,
			ease: "power2.inOut",
			y: 0,
		})
	}

	const content = (<Form
		{...formLayout}
		form={form}
		onFinish={(values) => {
			const DESCRIPTION_LONG_MIN_FOR_AI = 80;
			const descLong = (values?.descriptionLong || '').trim();
			const submit = () => {
				props.onChange?.(props.id || '', values);
				props.onHide();
			};
			if (
				!props.id &&
				values?.aiGenerate &&
				descLong.length < DESCRIPTION_LONG_MIN_FOR_AI &&
				!descLongWarningAcknowledgedRef.current
			) {
				Modal.confirm({
					title: t('models.project.fields.description_long_warning_title'),
					content: t('models.project.fields.description_long_warning_body', { count: descLong.length }),
					okText: t('models.project.fields.description_long_warning_enrich'),
					cancelText: t('models.project.fields.description_long_warning_continue'),
					onOk: () => {
						// User chose to enrich — abort submit and keep modal open.
					},
					onCancel: () => {
						descLongWarningAcknowledgedRef.current = true;
						submit();
					}
				});
				return;
			}
			submit();
		}}
		initialValues={{
			...props.values,
			likelihoods,
			impacts,
			riskCategories,
			indicatorCategories,
			type,
			aiScenarios: props.values?.aiScenarios && props.values.aiScenarios.length > 0 ? props.values.aiScenarios : defaultAiScenarios(t)
		}}
	>
		<Tabs>
			<Tabs.TabPane tab="General" key="general">

				<Flex>
					<Form.Item name="type">
						<Input type="hidden" />
					</Form.Item>
					<Form.Item
						name="reference"
						label={t('models.project.fields.reference')}
						className="mr-1"
						style={{ width: '20%' }}
						rules={[
							{
								max: 5,
								message:
									t("messages.form.max_length_exceeded_error", { length: 5 })
							}
						]}
					>
						<Input
							autoComplete='off'
							count={{
								show: true,
								max: 5,
							}}
						></Input>
					</Form.Item>

					{props.name === undefined && (
						<Form.Item
							name="name"
							required={true}
							label={labelWithPrivacyChip(t('models.project.fields.name'))}
							style={{ width: '80%' }}
							rules={[
								{
									max: 50,
									message:
										t("messages.form.max_length_exceeded_error", { length: 50 })
								},
								{
									required: true,
									message: t("messages.form.required_error")
								}
							]}
						>
							<Input
								autoComplete='off'
								placeholder={t(
									'models.project.fields.name_explanation'
								)}
								count={{
									show: true,
									max: 50,
								}}
							></Input>
						</Form.Item>
					)}
				</Flex>

				<Form.Item
					name="description"
					label={labelWithPrivacyChip(t('models.project.fields.description'))}
					rules={[
						{
							max: 500,
							message:
								t("messages.form.max_length_exceeded_error", { length: 500 })
						}
					]}
				>
					<Input.TextArea
						rows={4}
						placeholder={t(
							'models.project.fields.description_explanation'
						)}
						count={{
							show: true,
							max: 500,
						}}
					></Input.TextArea>
				</Form.Item>

				<Form.Item
					name="vision"
					label={labelWithPrivacyChip(t('models.project.fields.vision'))}
					rules={[
						{
							max: 500,
							message:
								t("messages.form.max_length_exceeded_error", { length: 500 })
						}
					]}
				>
					<Input.TextArea
						rows={3}
						placeholder={t('models.project.fields.vision_explanation')}
						count={{
							show: true,
							max: 500,
						}}
					></Input.TextArea>
				</Form.Item>

				<Form.Item
					name="descriptionLong"
					label={labelWithPrivacyChip(t('models.project.fields.description_long'))}
					extra={aiGenerateWatch ? (
						<span className="ai-description-hint">
							{t('models.project.fields.description_long_hint')}
						</span>
					) : null}
					rules={[
						{
							max: 1000,
							message:
								t("messages.form.max_length_exceeded_error", { length: 1000 })
						}
					]}
				>
					<Input.TextArea
						rows={6}
						placeholder={t(
							'models.project.fields.description_long_explanation'
						)}
						count={{
							show: true,
							max: 1000,
						}}
					></Input.TextArea>
				</Form.Item>

				{!props.id && AI_GENERATION_ENABLED && (
					<Form.Item name="aiGenerate" valuePropName="checked" className="ai-generate-section">
						<Checkbox>
							<span className="ai-generate-inner">
								<span className="ai-generate-icon-wrapper">
									<FontAwesomeIcon icon={faWandMagicSparkles as any} />
								</span>
								<span className="ai-generate-text">
									<span className="ai-generate-title">{t('messages.ai_project_generation.checkbox_label')}</span>
									<span className="ai-generate-subtitle">{t('messages.ai_project_generation.checkbox_tooltip')}</span>
								</span>
							</span>
						</Checkbox>
					</Form.Item>
				)}

				{aiGenerateWatch && (
					<Form.Item
						name="aiContext"
						label={labelWithPrivacyChip(t('models.project.fields.ai_context', { defaultValue: "Précisions et axes d'analyse" }))}
						rules={[
							{
								max: 500,
								message: t("messages.form.max_length_exceeded_error", { length: 500 })
							}
						]}
					>
						<Input.TextArea
							rows={3}
							placeholder={t('models.project.fields.ai_context_explanation', {
								defaultValue: "Indiquez les angles, acteurs ou contraintes que l'analyse doit privilégier (ex. un concurrent en particulier, une dimension réglementaire, un territoire, une contrainte temporelle...)."
							})}
							count={{ show: true, max: 500 }}
						/>
					</Form.Item>
				)}

				{!props.id && (
					<>
						<Form.Item shouldUpdate={(p, c) => p.aiGenerate !== c.aiGenerate} noStyle>
							{({ getFieldValue }) =>
								getFieldValue('aiGenerate') ? (
									<div className="ai-disclosure">
										<div className="ai-preview">
											<Flex align="center" justify="space-between" className="ai-preview-header">
												<Flex align="center" gap={8}>
													<span className="ai-preview-title">
														{t('messages.ai_project_generation.preview_title', {
															defaultValue: 'Prévisualisation de la recherche'
														})}
													</span>
													<Tooltip color="rgba(255, 192, 130, 0.92)" overlayInnerStyle={{ color: '#5a2e08' }} overlayClassName="ai-zone-tooltip public" title={t('messages.ai_project_generation.disclosure_public_sub')}>
														<span className="ai-zone-label public xs">
															<FontAwesomeIcon icon={faGlobe as any} className="ai-zone-icon" />
															{t('messages.ai_project_generation.zone_public', { defaultValue: 'Transmis à IA publique' })}
														</span>
													</Tooltip>
												</Flex>
												<Tooltip
													title={t('messages.ai_project_generation.preview_refresh', {
														defaultValue: 'Régénérer la prévisualisation'
													})}
												>
													<Button
														size="small"
														type="text"
														loading={previewLoading}
														icon={<FontAwesomeIcon icon={faRotate as any} />}
														onClick={() => void runPreview()}
													/>
												</Tooltip>
											</Flex>
											<div className="ai-preview-hint">
												{t('messages.ai_project_generation.preview_hint', {
													defaultValue:
														"Reformulation neutre de votre sujet, sans trace d'intention. C'est exactement ce qui sera transmis à la recherche publique — seulement après votre validation."
												})}
											</div>
											<Form.Item name="aiSearchPreview" noStyle>
												<Input.TextArea
													readOnly
													autoSize={{ minRows: 2, maxRows: 6 }}
													className="ai-preview-text"
													placeholder={
														previewLoading
															? t('messages.please_wait', { defaultValue: 'Veuillez patienter…' })
															: t('messages.ai_project_generation.preview_placeholder', {
																defaultValue: "Renseignez au moins le titre du projet pour générer la prévisualisation."
															})
													}
												/>
											</Form.Item>
											{previewError && (
												<div className="ai-preview-error">{previewError}</div>
											)}
										</div>

										<Flex align="center" gap={8} className="ai-scenarios-header">
											<span className="ai-scenarios-title">
												{t('models.project.fields.ai.scenarios.title', { defaultValue: 'Scénarios à explorer' })}
											</span>
											<Tooltip color="rgba(183, 235, 143, 0.92)" overlayInnerStyle={{ color: '#1f4d0a' }} overlayClassName="ai-zone-tooltip private" title={t('messages.ai_project_generation.disclosure_private_sub')}>
												<span className="ai-zone-label private xs">
													<FontAwesomeIcon icon={faShieldCheck as any} className="ai-zone-icon" />
													{t('messages.ai_project_generation.field_private_chip', { defaultValue: 'Privé & sécurisé' })}
												</span>
											</Tooltip>
										</Flex>
										<div className="ai-scenarios">
											<Form.List name="aiScenarios">
												{(fields, { add, remove }) => (
													<>
														{fields.map((field) => (
															<div key={field.key} className="ai-scenario-card">
																<Flex gap={8} align="flex-start" className="ai-scenario-outer">
																	<Tooltip
																		title={t('models.project.fields.ai.scenarios.remove', {
																			defaultValue: 'Retirer ce scénario'
																		})}
																	>
																		<Button
																			className="ai-scenario-remove"
																			onClick={() => remove(field.name)}
																		>
																			<FontAwesomeIcon icon={faTrash as any} />
																		</Button>
																	</Tooltip>
																	<div className="ai-scenario-fields">
																		<Flex gap={8} align="center" className="ai-scenario-row">
																			<Form.Item
																				name={[field.name, 'label']}
																				noStyle
																				rules={[{ required: true, message: t('messages.form.required_error') }]}
																			>
																				<Input
																					placeholder={t('models.project.fields.ai.scenarios.label_placeholder', {
																						defaultValue: 'Nom du scénario'
																					})}
																					className="ai-scenario-label"
																				/>
																			</Form.Item>
																			<Flex gap={6} align="center" className="ai-scenario-horizon-wrap">
																				<Form.Item name={[field.name, 'horizonValue']} noStyle>
																					<InputNumber min={1} max={9999} className="ai-scenario-horizon-value" controls={false} />
																				</Form.Item>
																				<Form.Item name={[field.name, 'horizonUnit']} noStyle>
																					<Select className="ai-scenario-horizon-unit" options={HORIZON_UNIT_OPTIONS(t)} />
																				</Form.Item>
																			</Flex>
																		</Flex>
																		<Form.Item name={[field.name, 'content']} noStyle>
																			<Input.TextArea
																				autoSize={{ minRows: 2, maxRows: 6 }}
																				placeholder={t('models.project.fields.ai.scenarios.content_placeholder', {
																					defaultValue: 'Hypothèses, événements clés, conditions de réalisation…'
																				})}
																				className="ai-scenario-content"
																			/>
																		</Form.Item>
																	</div>
																</Flex>
															</div>
														))}

														<Button
															type="dashed"
															icon={<FontAwesomeIcon icon={faPlus as any} />}
															onClick={() =>
																add({
																	label: '',
																	content: '',
																	horizonValue: 6,
																	horizonUnit: 'm'
																})
															}
															className="ai-scenario-add"
														>
															{t('models.project.fields.ai.scenarios.add', { defaultValue: 'Ajouter un scénario' })}
														</Button>
													</>
												)}
											</Form.List>
										</div>
									</div>
								) : null
							}
						</Form.Item>
					</>
				)}

				{(props.id || aiGenerateWatch) && (
					<div className="project-attachments-block">
						<AttachmentsSection
							projectId={props.id}
							tempSessionId={!props.id ? tempSessionId : undefined}
						/>
					</div>
				)}

			</Tabs.TabPane>

			{type !== "stakeholders" && <Tabs.TabPane tab={t('pages.projects.tabs.risks.title')} key="risks">
				<div className="ant-col ant-col-24 ant-form-item-label mb-05 noselection">
					<strong>
						{t('models.risk_category.name_plural')}
					</strong>
				</div>
				<Form.List name="riskCategories">
					{(fields, { add, remove }) => {
						return (
							<div>

								{fields.map((field, index) => {
									return (
										<Flex
											key={index}
											className="mb-05"
										>
											<Button
												className="mr-1 mb-0"
												onClick={() =>
													remove(index)
												}
											>
												<FontAwesomeIcon
													icon={faTrash as any}
												/>
											</Button>

											<Form.Item
												name={[
													field.name,
													'id'
												]}
												noStyle
											>
												<Input type="hidden" />
											</Form.Item>

											<Form.Item
												name={[
													field.name,
													'name'
												]}
												className="mb-0 w-50"
												required={true}
												rules={[
													{
														max: 20,
														message:
															t("messages.form.max_length_exceeded_error", { length: 20 })
													},
													{
														required: true,
														message: t("messages.form.required_error")
													}
												]}
											>
												<Input autoComplete='off' className="mb-0" placeholder={t('models.risk_category.fields.name')} count={{
													show: true,
													max: 20,
												}} />
											</Form.Item>

											<Form.Item
												name={[
													field.name,
													'weight'
												]}
												className="ml-1 mb-0"
											>
												<FormWeight dark={true}></FormWeight>

											</Form.Item>
										</Flex>

									);
								})}

								{fields.length > 0 && <Flex className="mb-2">
									<Button
										onClick={() => add({ weight: 1 })}
										className="mb-0 mr-4"
									>
										<FontAwesomeIcon
											icon={faPlus as any}
										/>
									</Button>
								</Flex>}

								{fields.length == 0 && (
									<Empty description={false}>
										{t('models.risk_category.messages.no_x')}.{' '}
										<Trans
											i18nKey="messages.click_to_create_one"
											components={{
												button: (
													<Button onClick={() => add()} size="small">
														<FontAwesomeIcon icon={faPlus as any} />
													</Button>
												)
											}}
										/>
									</Empty>
								)}

							</div>
						);
					}}
				</Form.List>

				<div className="ant-col ant-col-24 ant-form-item-label mt-2 mb-05">
					<strong>
						{t('models.project.fields.impacts')}
					</strong>
				</div>
				<Form.List name="impacts">
					{(fields, { add, remove }) => {
						return (
							<div>

								{(fields).map((field, index) => {
									return (
										<>
											<Flex
												key={index}
												className="mb-05"
											>
												<Button
													className="mr-1 mb-0"
													disabled={fields.length > index + 1 || index < 3}
													onClick={() =>
														remove(index)
													}
												>
													<FontAwesomeIcon
														icon={faTrash as any}
													/>
												</Button>

												<Form.Item
													name={[
														field.name,
														'id'
													]}
													className="mb-0 mt-0 mr-0"
												>
													<Input value={(index + 1).toString()} type="hidden" />
												</Form.Item>

												<div style={{ width: "20px", height: "30px" }} className="d-flex align-items-center justify-content-center mr-1">{index + 1}</div>

												<Form.Item
													name={[
														field.name,
														'name'
													]}
													className="mb-0 w-50"
												>
													<Input autoComplete='off' placeholder={t('models.risk.fields.impact_level')} />
												</Form.Item>
											</Flex>

										</>
									);
								})}

								<div className="d-flex mb-2 align-items-center">
									<Button
										onClick={() => add()}
										className="mb-0 mr-4"
									>
										<FontAwesomeIcon
											icon={faPlus as any}
										/>
									</Button>
								</div>


							</div>
						);
					}}
				</Form.List>

				<div className="ant-col ant-col-24 ant-form-item-label mt-2 mb-05">
					<strong>
						{t('models.project.fields.likelihoods')}
					</strong>
				</div>
				<Form.List name="likelihoods">
					{(fields, { add, remove }) => {
						return (
							<div>

								{(fields).map((field, index) => {
									return (

										<Flex
											key={index}
											className="mb-05"
										>
											<Button
												className="mr-1 mb-0"
												disabled={fields.length > index + 1 || index < 3}
												onClick={() =>
													remove(index)
												}
											>
												<FontAwesomeIcon
													icon={faTrash as any}
												/>
											</Button>

											<Form.Item
												name={[
													field.name,
													'id'
												]}
												className="mb-0 mt-0 mr-0"
											>
												<Input value={(index + 1).toString()} type="hidden" />
											</Form.Item>

											<div style={{ width: "20px", height: "30px" }} className="d-flex align-items-center justify-content-center mr-1">{index + 1}</div>

											<Form.Item
												name={[
													field.name,
													'name'
												]}
												className="mb-0 w-50"
											>
												<Input autoComplete='off' placeholder={t('models.risk.fields.likelihood_level')} />
											</Form.Item>
										</Flex>


									);
								})}

								<div className="d-flex mb-2 align-items-center">
									<Button
										onClick={() => add()}
										className="mb-0 mr-4"
									>
										<FontAwesomeIcon
											icon={faPlus as any}
										/>
									</Button>
								</div>


							</div>
						);
					}}
				</Form.List>


			</Tabs.TabPane>}
			<Tabs.TabPane tab={t('models.indicator.name_plural')} key="indicators">
				<div className="ant-col ant-col-24 ant-form-item-label mb-05 noselection">
					<strong>
						{t('models.indicator_category.name_plural')}
					</strong>
				</div>
				<Form.List name="indicatorCategories">
					{(fields, { add, remove }) => {
						return (
							<div>

								{fields.map((field, index) => {
									return (
										<Flex
											key={index}
											className="mb-05"
										>
											<Button
												className="mr-1 mb-0"
												onClick={() =>
													remove(index)
												}
											>
												<FontAwesomeIcon
													icon={faTrash as any}
												/>
											</Button>

											<Form.Item
												name={[
													field.name,
													'id'
												]}
												noStyle
											>
												<Input type="hidden" />
											</Form.Item>

											<Form.Item
												name={[
													field.name,
													'name'
												]}
												className="mb-0 w-50"
												required={true}
												rules={[
													{
														max: 30,
														message:
															t("messages.form.max_length_exceeded_error", { length: 30 })
													},
													{
														required: true,
														message: t("messages.form.required_error")
													}
												]}
											>
												<Input autoComplete='off' className="mb-0" placeholder={t('models.indicator_category.fields.name')} count={{
													show: true,
													max: 30,
												}} />
											</Form.Item>

										</Flex>

									);
								})}

								{fields.length > 0 && <Flex className="mb-2">
									<Button
										onClick={() => add({})}
										className="mb-0 mr-4"
									>
										<FontAwesomeIcon
											icon={faPlus as any}
										/>
									</Button>
								</Flex>}

								{fields.length == 0 && (
									<Empty description={false}>
										{t('models.indicator_category.messages.no_x')}.{' '}
										<Trans
											i18nKey="messages.click_to_create_one"
											components={{
												button: (
													<Button onClick={() => add({})} size="small">
														<FontAwesomeIcon icon={faPlus as any} />
													</Button>
												)
											}}
										/>
									</Empty>
								)}

							</div>
						);
					}}
				</Form.List>
			</Tabs.TabPane>
			<Tabs.TabPane tab={t('models.tag.name_plural')} key="tags">
				<Form.List name="tags">
					{(fields, { add, remove }) => {
						return (
							<div>
								{fields.map((field, index) => {
									return (
										<Flex
											key={index}
											className="mb-05"
										>
											<Button
												className="mr-1 mb-0"
												onClick={() =>
													remove(index)
												}
											>
												<FontAwesomeIcon
													icon={faTrash as any}
												/>
											</Button>

											<Form.Item
												name={[
													field.name,
													'id'
												]}
												noStyle
											>
												<Input type="hidden" />
											</Form.Item>

											<Form.Item
												name={[
													field.name,
													'name'
												]}
												className="mb-0"
											>
												<Input autoComplete='off' />
											</Form.Item>
										</Flex>
									);
								})}

								{fields.length > 0 && <div className="d-flex mb-2 align-items-center">
									<Button
										onClick={() => add()}
										className="mb-0 mr-4"
									>
										<FontAwesomeIcon
											icon={faPlus as any}
										/>
									</Button>
								</div>}

								{fields.length == 0 && (
									<Empty description={false}>
										{t('models.tag.messages.no_x')}.{' '}
										<Trans
											i18nKey="messages.click_to_create_one"
											components={{
												button: (
													<Button onClick={() => add()} size="small">
														<FontAwesomeIcon icon={faPlus as any} />
													</Button>
												)
											}}
										/>
									</Empty>
								)}
							</div>
						);
					}}


				</Form.List>
			</Tabs.TabPane>
			{props.id && props.values?.myRole !== 'reader' && (
				<Tabs.TabPane tab={t('models.project.share.tab', { defaultValue: 'Partage' })} key="share">
					<ProjectShareTab projectId={props.id} />
				</Tabs.TabPane>
			)}
		</Tabs>
	</Form>)

	const contentWhenNotTyped = (<>
		<h3>{t("messages.create_project_title")}</h3>
		<Flex className='mt-1 mb-2'>

			<Card className={`ProjectType w-100 mt-4 mb-2 mr-1 text-center ${props.authorizedTypes?.stakeholders ? '' : 'disabled'}`} onMouseLeave={removeProjectTypeHover} onMouseOver={applyProjectTypeHover} onClick={() => { if (!!props.authorizedTypes["stakeholders"]) setType("stakeholders") }}>
				<FontAwesomeIcon size={"4x"} color="#BBBBBB" icon={faChartNetwork as any} />

				<h4 className="mt-4">{t("models.project.fields.types.stakeholders_explanation")}</h4>
				<div>
					{t("models.project.fields.types.stakeholders")}
				</div>
				<div className="upgradeRequired">{t("messages.upgrade_required")}</div>
			</Card>
			<Card className={`ProjectType w-100 mt-4 mb-2 mr-1 text-center ${props.authorizedTypes?.risks ? '' : 'disabled'}`} onMouseLeave={removeProjectTypeHover} onMouseOver={applyProjectTypeHover} onClick={() => { if (!!props.authorizedTypes["risks"]) setType("risks") }}>
				<FontAwesomeIcon size={"4x"} color="#BBBBBB" icon={faShieldExclamation as any} />
				<h4 className="mt-4">{t("models.project.fields.types.risks_explanation")}</h4>
				<div>
					{t("models.project.fields.types.risks")}
				</div>
				<div className="upgradeRequired">{t("messages.upgrade_required")}</div>
			</Card>
			<Card className={`ProjectType w-100 mt-4 mb-2 mr-1 text-center ${props.authorizedTypes?.pro ? '' : 'disabled'}`} onMouseLeave={removeProjectTypeHover} onMouseOver={applyProjectTypeHover} onClick={() => { if (!!props.authorizedTypes["pro"]) setType("pro") }}>
				<FontAwesomeIcon size={"4x"} color="#BBBBBB" icon={faChess as any} />
				<h4 className="mt-4">{t("models.project.fields.types.pro_explanation")}</h4>
				<div>
					{t("models.project.fields.types.pro")}
				</div>
				<div className="upgradeRequired">{t("messages.upgrade_required")}</div>
			</Card>

		</Flex>
	</>)

	/* Organize with tabs */
	return (
		<Modal
			open={true}
			okButtonProps={{ style: { display: 'none' } }}
			onCancel={props.onHide}
			cancelText={t('actions.close')}
			footer={footer}
			width={'60%'}
			maskClosable={false}
			className='ProjectModal'
		>
			{type !== null ? content : contentWhenNotTyped}
		</Modal>
	);
};

export default ProjectModal;
export { defaultRiskCategories, defaultIndicatorCategories, defaultLikelihoods, defaultImpacts };